import React from 'react';

import { motion } from 'framer-motion';
import { ArrowRight, BarChart2, FileCheck, Lightbulb, Target } from 'lucide-react';

import ModernButton from './ui/ModernButton';

interface BusinessDiagnosticProps {
  className?: string;
}

const BusinessDiagnostic: React.FC<BusinessDiagnosticProps> = ({ className = '' }) => {
  // URL del formulario de diagnóstico
  const diagnosticFormUrl =
    'https://docs.google.com/forms/d/e/1FAIpQLSe6Hyn1LcxayvpNBAd5QkqSA6tls2kw0YkY3FbUQGdrD-l-Hg/viewform';

  // Beneficios del diagnóstico
  const diagnosticBenefits = [
    {
      icon: <FileCheck className="h-6 w-6 text-blue-500" />,
      title: 'Análisis Completo',
      description:
        'Evaluación detallada de la situación actual de su empresa y áreas de oportunidad',
    },
    {
      icon: <BarChart2 className="h-6 w-6 text-indigo-500" />,
      title: 'Datos Relevantes',
      description: 'Obtención de métricas clave para la toma de decisiones estratégicas',
    },
    {
      icon: <Lightbulb className="h-6 w-6 text-amber-500" />,
      title: 'Recomendaciones Personalizadas',
      description: 'Sugerencias específicas adaptadas a las necesidades de su negocio',
    },
    {
      icon: <Target className="h-6 w-6 text-red-500" />,
      title: 'Plan de Acción',
      description: 'Hoja de ruta clara con pasos concretos para implementar mejoras',
    },
  ];

  return (
    <section className={`py-16 my-8 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          whileHover={{ scale: 1.02 }}
          transition={{ type: 'spring', stiffness: 300, damping: 10 }}
          className="bg-gradient-to-br from-blue-700 to-indigo-900 rounded-2xl shadow-2xl overflow-hidden border border-blue-400 relative"
        >
          {/* Elementos decorativos de fondo */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500 opacity-10 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-indigo-500 opacity-10 rounded-full -ml-24 -mb-24"></div>
          <div className="absolute top-1/3 right-1/4 w-16 h-16 bg-yellow-400 opacity-10 rounded-full"></div>
          <div className="absolute bottom-1/4 left-1/3 w-24 h-24 bg-blue-300 opacity-10 rounded-full"></div>

          {/* Partículas decorativas animadas */}
          <div
            className="absolute top-1/4 right-1/4 w-3 h-3 bg-yellow-300 rounded-full opacity-70 animate-pulse"
            style={{ animationDuration: '4s' }}
          ></div>
          <div
            className="absolute bottom-1/3 left-1/3 w-2 h-2 bg-blue-200 rounded-full opacity-80 animate-pulse"
            style={{ animationDuration: '3.5s' }}
          ></div>
          <div
            className="absolute top-2/3 right-1/2 w-4 h-4 bg-indigo-300 rounded-full opacity-60 animate-pulse"
            style={{ animationDuration: '5s' }}
          ></div>

          <div className="px-8 py-16 md:p-16 md:flex md:items-center md:justify-between relative z-10">
            <div className="md:w-3/5 mb-10 md:mb-0 md:pr-10">
              <div className="mb-4">
                <span className="bg-blue-900 bg-opacity-50 text-blue-200 text-xs font-medium px-4 py-1.5 rounded-full uppercase tracking-wider border border-blue-400">
                  Servicio Gratuito
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
                Diagnóstico Empresarial <span className="text-blue-300">Gratuito</span>
              </h2>
              <p className="text-blue-100 mb-8 text-lg">
                Descubra el potencial oculto de su empresa con nuestro diagnóstico empresarial
                exclusivo. Obtenga un análisis detallado y recomendaciones personalizadas para
                impulsar el crecimiento de su negocio.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {diagnosticBenefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <div className="bg-white bg-opacity-10 p-2 rounded-lg mr-3 flex-shrink-0">
                      {benefit.icon}
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">{benefit.title}</h3>
                      <p className="text-blue-200 text-sm">{benefit.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="text-blue-200 text-sm bg-blue-800 bg-opacity-30 p-4 rounded-lg mb-6">
                <p className="italic">
                  <span className="font-semibold">Nota:</span> Este diagnóstico es completamente
                  gratuito y sin compromiso. Los datos proporcionados serán tratados con absoluta
                  confidencialidad y solo se utilizarán para elaborar su informe personalizado.
                </p>
              </div>
            </div>

            <div className="md:w-2/5 relative z-10">
              <div className="bg-white bg-opacity-80 p-6 rounded-xl backdrop-blur-sm border border-white border-opacity-20 shadow-xl">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">Solicite su Diagnóstico</h3>
                  <p className="text-gray-700 text-sm">
                    Complete el cuestionario y reciba su informe personalizado en menos de 48 horas.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center text-gray-700 text-sm">
                    <div className="bg-blue-600 text-white rounded-full h-6 w-6 flex items-center justify-center mr-3">
                      <span className="text-xs font-bold">1</span>
                    </div>
                    <span>Complete el cuestionario (5-10 min)</span>
                  </div>

                  <div className="flex items-center text-gray-700 text-sm">
                    <div className="bg-blue-600 text-white rounded-full h-6 w-6 flex items-center justify-center mr-3">
                      <span className="text-xs font-bold">2</span>
                    </div>
                    <span>Nuestro equipo analizará sus respuestas</span>
                  </div>

                  <div className="flex items-center text-gray-700 text-sm">
                    <div className="bg-blue-600 text-white rounded-full h-6 w-6 flex items-center justify-center mr-3">
                      <span className="text-xs font-bold">3</span>
                    </div>
                    <span>Reciba su diagnóstico personalizado</span>
                  </div>
                </div>

                <div className="mt-8">
                  <a href={diagnosticFormUrl} target="_blank" rel="noopener noreferrer">
                    <ModernButton variant="accent" size="lg" className="w-full shadow-lg">
                      Iniciar Diagnóstico Gratuito
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </ModernButton>
                  </a>
                </div>

                <div className="mt-4 text-center">
                  <p className="text-gray-600 text-xs">
                    Plazas limitadas disponibles para este mes
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BusinessDiagnostic;
